import SignatureCanvas from "react-signature-canvas";
import { useContext, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRefresh } from "@fortawesome/free-solid-svg-icons";
import BaseFormModal from "./utils/BaseFormModal";
import {
  getReportSignatureAuto,
  savePCRSAssessmentSignature,
} from "../paths/pcrs";
import { ToastMessageContext } from "../contexts/ToastMessageContext";
import { buildSyspapURI } from "../utils";
import IconButton from "../components/IconButton";

export default function SignatureModal({ assessmentID }) {
  const signatureCanvasRef = useRef(null);
  const { toastMessage } = useContext(ToastMessageContext);
  const [showPinPrompt, setShowPinPrompt] = useState(false);
  const [pinValue, setPinValue] = useState("");
  const [pendingSignatureData, setPendingSignatureData] = useState(null);

  const closePinPrompt = () => {
    setShowPinPrompt(false);
    setPinValue("");
  };

  const clearSignature = () => {
    signatureCanvasRef.current.clear();
  };

  const handleAutoSignature = async () => {
    let result = await getReportSignatureAuto(pinValue);
    if (!result.success) {
      toastMessage(
        result.error || "Une erreur est survenue lors de la validation du NIP",
        "danger"
      );
      return false;
    }
    setPendingSignatureData(`data:${result.signatureData}`);
    closePinPrompt();
    return false;
  };

  const saveSignature = async (formData) => {
    let result = await savePCRSAssessmentSignature(formData);
    if (!result.success) {
      toastMessage(
        "Une erreur est survenue lors de la sauvegarde de la signature",
        "danger"
      );
    } else {
      toastMessage("La signature a été sauvegardée avec succès", "success");
      setTimeout(() => {
        redirectToSyspapOnSignatureSaved(result.successURL);
      }, 1000);
    }
    return result.success;
  };

  const getSignatureData = () => {
    return {
      signature: signatureCanvasRef.current.toDataURL(),
      assessmentID: assessmentID,
    };
  };

  const validateSignature = (data) => {
    if (data.signature === null) {
      return ["Veuillez signer le rapport"];
    }
    return [];
  };

  const validatePinPrompt = () => {
    if (!pinValue || !/^\d{4}$/.test(pinValue)) {
      return ["Veuillez entrer un NIP valide (4 chiffres)"];
    }
    return [];
  };

  const redirectToSyspapOnSignatureSaved = (successUrl) => {
    let uri = buildSyspapURI(successUrl);
    window.location.replace(uri);
  };

  const signatoryRaw = window.localStorage.getItem(
    process.env.REACT_APP_TITLE_STORAGE_NAME
  );

  const signatoryParts = (() => {
    if (!signatoryRaw) return { name: "", titles: "" };
    try {
      const parsed = JSON.parse(signatoryRaw);
      if (
        parsed &&
        typeof parsed.name === "string" &&
        typeof parsed.titles === "string"
      ) {
        return { name: parsed.name, titles: parsed.titles };
      }
    } catch (_) {}
    const commaIndex = signatoryRaw.indexOf(",");
    if (commaIndex >= 0) {
      return {
        name: signatoryRaw.slice(0, commaIndex).trim() + ",",
        titles: signatoryRaw.slice(commaIndex + 1).trim(),
      };
    }
    return { name: signatoryRaw, titles: "" };
  })();

  const displayName = signatoryParts.name.replace(/\s*,\s*$/, "").trim();
  const displayTitles = signatoryParts.titles
    ? signatoryParts.titles.trim()
    : "";

  const leftFooterButtons = (
    <IconButton
      icon={faRefresh}
      variant="outline-secondary"
      type="button"
      onClick={() => setShowPinPrompt(true)}
      className="d-flex align-items-center"
    >
      Utilisez ma signature générée
    </IconButton>
  );

  useEffect(() => {
    if (showPinPrompt || !pendingSignatureData || !signatureCanvasRef.current) {
      return;
    }
    try {
      clearSignature();
      signatureCanvasRef.current.fromDataURL(pendingSignatureData);
    } catch (error) {
      toastMessage("Impossible de charger la signature sauvegardee", "danger");
    } finally {
      setPendingSignatureData(null);
    }
  }, [showPinPrompt, pendingSignatureData, toastMessage]);

  if (showPinPrompt) {
    return (
      <BaseFormModal
        modalTitle="Signature automatique"
        submitBtnText="Utiliser la signature"
        submitFct={handleAutoSignature}
        validateForm={validatePinPrompt}
        onCancel={closePinPrompt}
      >
        <div className="col-12 align-items-center">
          <label className="form-label">NIP</label>
          <input
            className="form-control"
            maxLength="4"
            inputMode="numeric"
            pattern="[0-9]*"
            type="password"
            autoComplete="off"
            value={pinValue}
            onChange={(event) => setPinValue(event.target.value)}
            placeholder="Entrez votre NIP à 4 chiffres"
            autoFocus
          />
          <small className="form-text text-muted mt-2 d-block">
            Veuillez entrer votre NIP à 4 chiffres pour récupérer votre
            signature sauvegardée
          </small>
        </div>
      </BaseFormModal>
    );
  }

  return (
    <BaseFormModal
      modalTitle="Signer le rapport"
      submitBtnText="Signer"
      submitFct={saveSignature}
      getFormDataFct={getSignatureData}
      validateForm={validateSignature}
      extraLeftFooterButtons={leftFooterButtons}
    >
      <div className="col-12 mb-3 text-center signature-canvas-container">
        <SignatureCanvas
          ref={signatureCanvasRef}
          penColor="black"
          canvasProps={{ className: "signature-canvas" }}
        />
        <IconButton
          icon={faRefresh}
          variant="link"
          type="button"
          onClick={clearSignature}
          className="clear-canva-btn"
          title="Effacer la signature"
        />
      </div>
      <div className="col-12">
        <div className="bg-light p-3 rounded">
          <p className="m-0">
            <span className="fw-bold text-primary">Signataire: </span>
            <span className="text-dark">
              <span style={{ whiteSpace: "nowrap" }}>
                {displayName}
                {displayTitles ? "," : ""}
              </span>
              {displayTitles ? ` ${displayTitles}` : ""}
            </span>
          </p>
        </div>
      </div>
    </BaseFormModal>
  );
}
